/**
 * BRAND AUTOCOMPLETE COMPONENT
 * 
 * Helps users enter brand names consistently by:
 * - Suggesting existing brands from their collection
 * - Suggesting known brands from the brand database
 * - Showing brand category (indie/boutique/mainstream) badges
 * - Preventing duplicate brand entries with different spellings
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { findKnownCategory, getCategoryDisplay, KNOWN_INDIE_BRANDS, KNOWN_BOUTIQUE_BRANDS, KNOWN_MAINSTREAM_BRANDS, type BrandCategory } from '../../types/brandCategories';

// Combined known brands list - defined outside component to prevent recreation
const ALL_KNOWN_BRANDS = [...KNOWN_INDIE_BRANDS, ...KNOWN_BOUTIQUE_BRANDS, ...KNOWN_MAINSTREAM_BRANDS];

// Stable module-level defaults. Using `= []` / `= {}` as default parameter
// values creates a fresh array/object every render — which flows through the
// suggestion-building useEffect's deps and causes it to re-fire on every
// parent re-render. On tools (where the parent didn't pass these props) that
// manifests as the dropdown re-opening right after you click a suggestion,
// visually "swallowing" the selection. A stable reference breaks that cycle.
const EMPTY_EXISTING_BRANDS: string[] = [];
const EMPTY_USER_BRAND_CATEGORIES: Record<string, BrandCategory> = {};
const EMPTY_FIRESTORE_BRANDS: Array<{ name: string; status?: string; curationStatus?: string }> = [];

interface BrandAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  existingBrands?: string[]; // Brands already in user's collection
  userBrandCategories?: Record<string, BrandCategory>; // User's custom categorizations
  /**
   * Canonicalized brands from the Firestore `brands` collection. Passed
   * in by the caller (usually via `useFirestoreBrands()`) so the
   * component itself stays framework/data-agnostic. Ranked after the
   * user's own collection but before the hardcoded known-brands list —
   * these are admin-canonicalized, so a match here is the "right"
   * spelling.
   */
  firestoreBrands?: Array<{ name: string; status?: string; curationStatus?: string }>;
  placeholder?: string;
  className?: string;
  required?: boolean;
  disabled?: boolean;
}

interface BrandSuggestion {
  name: string;
  category: BrandCategory | null;
  source: 'collection' | 'firestore' | 'known';
  /** True when the Firestore brand doc was marked verified or
   *  admin-canonicalized. Feeds a small "✓ Canonical" badge in the
   *  dropdown row so users see they're picking the "authoritative" one. */
  canonical?: boolean;
}

const BrandAutocomplete: React.FC<BrandAutocompleteProps> = ({
  value,
  onChange,
  existingBrands = EMPTY_EXISTING_BRANDS,
  userBrandCategories = EMPTY_USER_BRAND_CATEGORIES,
  firestoreBrands = EMPTY_FIRESTORE_BRANDS,
  placeholder = 'e.g., ILNP',
  className = '',
  required = false,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<BrandSuggestion[]>([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  // When the user picks a suggestion, `onChange` fires with the picked value —
  // which re-enters the value-change effect below and, because the selected
  // brand still matches itself in the search, would immediately re-open the
  // panel. This ref suppresses that one re-open.
  const justSelectedRef = useRef(false);

  // Memoize existing brands to prevent unnecessary recalculations
  const normalizedExistingBrands = useMemo(() => existingBrands, [existingBrands]);

  // Generate suggestions based on input
  useEffect(() => {
    if (justSelectedRef.current) {
      justSelectedRef.current = false;
      setSuggestions([]);
      setIsOpen(false);
      return;
    }
    if (!value || value.length < 1) {
      setSuggestions([]);
      setIsOpen(false);
      return;
    }

    const searchTerm = value.toLowerCase().trim();
    const results: BrandSuggestion[] = [];
    const addedBrands = new Set<string>();

    // First, search user's existing brands (prioritize these)
    normalizedExistingBrands.forEach(brand => {
      if (brand.toLowerCase().includes(searchTerm) && !addedBrands.has(brand.toLowerCase())) {
        const category = userBrandCategories[brand] || findKnownCategory(brand);
        results.push({
          name: brand,
          category,
          source: 'collection'
        });
        addedBrands.add(brand.toLowerCase());
      }
    });

    // Then, search Firestore-canonicalized brands (admin-curated). These
    // outrank the hardcoded known list because a match here reflects the
    // admin's actual canonicalization; the hardcoded list is a static
    // fallback for brands the admin hasn't touched yet.
    firestoreBrands.forEach((b) => {
      if (
        b.name.toLowerCase().includes(searchTerm) &&
        !addedBrands.has(b.name.toLowerCase())
      ) {
        const canonical =
          b.status === 'verified' || b.curationStatus === 'canonical';
        results.push({
          name: b.name,
          category: userBrandCategories[b.name] || findKnownCategory(b.name),
          source: 'firestore',
          canonical,
        });
        addedBrands.add(b.name.toLowerCase());
      }
    });

    // Then, search known brands (if not already added)
    ALL_KNOWN_BRANDS.forEach(brand => {
      if (brand.toLowerCase().includes(searchTerm) && !addedBrands.has(brand.toLowerCase())) {
        results.push({
          name: brand,
          category: findKnownCategory(brand),
          source: 'known'
        });
        addedBrands.add(brand.toLowerCase());
      }
    });

    // Sort: exact matches first, then starts-with, then contains
    results.sort((a, b) => {
      const aLower = a.name.toLowerCase();
      const bLower = b.name.toLowerCase();
      
      // Exact match
      if (aLower === searchTerm && bLower !== searchTerm) return -1;
      if (bLower === searchTerm && aLower !== searchTerm) return 1;
      
      // Starts with
      const aStarts = aLower.startsWith(searchTerm);
      const bStarts = bLower.startsWith(searchTerm);
      if (aStarts && !bStarts) return -1;
      if (bStarts && !aStarts) return 1;
      
      // Priority: user's collection → Firestore-canonical → hardcoded known
      const sourceRank: Record<BrandSuggestion['source'], number> = {
        collection: 0,
        firestore: 1,
        known: 2,
      };
      if (sourceRank[a.source] !== sourceRank[b.source]) {
        return sourceRank[a.source] - sourceRank[b.source];
      }

      // Alphabetical
      return a.name.localeCompare(b.name);
    });

    // Limit to 10 suggestions
    setSuggestions(results.slice(0, 10));
    setIsOpen(results.length > 0);
    setHighlightedIndex(-1);
  }, [value, normalizedExistingBrands, userBrandCategories, firestoreBrands]);

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0) {
          selectBrand(suggestions[highlightedIndex].name);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  // Select a brand
  const selectBrand = (brandName: string) => {
    justSelectedRef.current = true;
    onChange(brandName);
    setIsOpen(false);
    setHighlightedIndex(-1);
    // Blur (not refocus) — refocusing re-triggers onFocus, which re-opens the
    // panel and hides the next form field below.
    inputRef.current?.blur();
  };

  // Get category badge styles
  const getCategoryBadge = (category: BrandCategory | null) => {
    if (!category) return null;
    const display = getCategoryDisplay(category);
    return (
      <span className={`ml-2 px-1.5 py-0.5 rounded text-xs font-medium ${display.color}`}>
        {display.emoji} {display.label}
      </span>
    );
  };

  // Detected category for current value
  const detectedCategory = findKnownCategory(value) || userBrandCategories[value];

  return (
    <div className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => value && suggestions.length > 0 && setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={`input-light font-body ${className}`}
          required={required}
          disabled={disabled}
          autoComplete="off"
          role="combobox"
          aria-expanded={isOpen}
          aria-autocomplete="list"
          aria-controls="brand-listbox"
          aria-activedescendant={highlightedIndex >= 0 ? `brand-option-${highlightedIndex}` : undefined}
        />
        {/* Show detected category badge next to input */}
        {value && detectedCategory && !isOpen && (
          <div className="absolute right-2 top-1/2 -translate-y-1/2">
            {getCategoryBadge(detectedCategory)}
          </div>
        )}
      </div>

      {/* Suggestions dropdown */}
      {isOpen && suggestions.length > 0 && (
        <div
          ref={dropdownRef}
          id="brand-listbox"
          role="listbox"
          aria-label="Brand suggestions"
          className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
        >
          {suggestions.map((suggestion, index) => (
            <button
              key={`${suggestion.name}-${index}`}
              id={`brand-option-${index}`}
              type="button"
              role="option"
              aria-selected={index === highlightedIndex}
              onClick={() => selectBrand(suggestion.name)}
              onMouseEnter={() => setHighlightedIndex(index)}
              className={`w-full px-3 py-2 text-left flex items-center justify-between hover:bg-gray-50 ${
                index === highlightedIndex ? 'bg-blue-50' : ''
              } ${index === 0 ? 'rounded-t-lg' : ''} ${index === suggestions.length - 1 ? 'rounded-b-lg' : ''}`}
            >
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-900">{suggestion.name}</span>
                {suggestion.source === 'collection' && (
                  <span className="text-xs text-green-600 bg-green-50 px-1.5 py-0.5 rounded">
                    In collection
                  </span>
                )}
                {suggestion.source === 'firestore' && suggestion.canonical && (
                  <span
                    className="text-xs text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded"
                    title="Admin-canonicalized brand — picking this locks in the spelling used across the site."
                  >
                    ✓ Canonical
                  </span>
                )}
                {suggestion.source === 'firestore' && !suggestion.canonical && (
                  <span
                    className="text-xs text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded"
                    title="Known brand from the app's brands directory."
                  >
                    Known
                  </span>
                )}
              </div>
              {getCategoryBadge(suggestion.category)}
            </button>
          ))}
        </div>
      )}

      {/* Helper text */}
      {value && !detectedCategory && value.length >= 3 && !isOpen && (
        <p className="mt-1 text-xs text-amber-600">
          New brand - will be added to your collection
        </p>
      )}
    </div>
  );
};

export default BrandAutocomplete;
